// platform/app/src/pages/PackagesPage.tsx
import { Package } from 'lucide-react'
import {
  GithubLinkButton,
  GithubMark,
  PageHeader,
  SectionHeader,
} from '@loop3/ui'
import uiPkg from '@root/packages/ui/package.json'
import observabilityPkg from '@root/packages/observability/package.json'
import syncPkg from '@root/packages/sync/package.json'

interface PackageEntry {
  /** Full npm-style name, e.g. `@loop3/ui`. */
  name: string
  /** Current version (from package.json). */
  version: string
  description: string
  /** Short slug matching the folder under packages/, e.g. `ui`. */
  slug: string
}

const PACKAGES: PackageEntry[] = [uiPkg, observabilityPkg, syncPkg].map(
  (p) => ({
    name: p.name,
    version: p.version,
    description: p.description ?? '',
    slug: p.name.replace('@loop3/', ''),
  })
)

export function PackagesPage() {
  return (
    <div className="mx-auto max-w-7xl pb-16">
      <PageHeader
        eyebrow="L3OS Sync"
        title="Packages"
        subtitle="Shared workspace packages consumed by every Loop3 app. Each is a pnpm workspace member published at version listed; source lives in the monorepo."
        actions={
          <GithubLinkButton
            href="https://github.com/Loop3Studio/l3-os/tree/main/packages"
            label="View folder"
          />
        }
      />

      <section>
        <SectionHeader
          icon={Package}
          label="Workspace packages"
          meta={`${PACKAGES.length} ${PACKAGES.length === 1 ? 'entry' : 'entries'}`}
        />

        <div className="border-t border-border">
          {PACKAGES.map((p) => {
            const repoHref = `https://github.com/Loop3Studio/l3-os/tree/main/packages/${p.slug}`
            return (
              <a
                key={p.name}
                href={repoHref}
                target="_blank"
                rel="noopener noreferrer"
                className="group block border-b border-border py-4 px-4 hover:bg-[var(--l3-violet-soft)] transition-colors"
              >
                <div className="mb-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h3 className="text-sm font-medium transition-colors group-hover:text-[var(--l3-violet)]">
                    {p.name}
                  </h3>
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground">
                    v{p.version}
                  </span>
                </div>
                <p className="font-mono text-[0.7rem] leading-relaxed opacity-60">
                  {p.description}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] leading-relaxed opacity-60 transition-colors group-hover:text-[var(--l3-violet)] group-hover:underline">
                    <GithubMark className="size-3.5" />
                    packages/{p.slug}
                  </span>
                </div>
              </a>
            )
          })}
        </div>
      </section>
    </div>
  )
}
